import {
  isFriendlyConversationIdValid,
  parseFriendlyConversationId,
} from '@provision/formatting-utils';
import { useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import { useConversations } from 'src/graphql';
import useConversationViewsStore from 'src/store/conversation-views';
import { AppEvents, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';
import { isUUID } from 'src/utils/uuid';

export const useSelectedConversation = ({
  conversationId: someConversationId,
  includeAttachments = false,
}: {
  conversationId: string;
  includeAttachments?: boolean;
}) => {
  const { friendlyIdToUuidMap } = useConversationViewsStore();

  // eslint-disable-next-line prefer-const
  let [conversationId, friendlyConversationId] = isUUID(someConversationId)
    ? [someConversationId, null]
    : [null, someConversationId];

  if (friendlyConversationId) {
    conversationId = friendlyIdToUuidMap[friendlyConversationId];
  }

  const conversationIdFilters = useMemo(() => {
    if (conversationId) {
      return [
        {
          attribute: 'conversationId',
          value: conversationId,
          operator: '=',
        },
      ];
    } else if (
      friendlyConversationId &&
      isFriendlyConversationIdValid(friendlyConversationId)
    ) {
      const { workspaceConversationId, partnerId } =
        parseFriendlyConversationId(friendlyConversationId);

      const filters = [];

      if (workspaceConversationId && Number(workspaceConversationId)) {
        filters.push({
          attribute: 'workspaceConversationId',
          value: workspaceConversationId,
          operator: '=',
        });
      }

      if (!isNullish(partnerId)) {
        filters.push({
          attribute: 'partnerId',
          value: partnerId,
          operator: '=',
        });
      }

      return filters;
    }

    return [];
  }, [friendlyConversationId, conversationId]);

  const shouldSkipQuery = Boolean(
    !someConversationId ||
      (friendlyConversationId &&
        !conversationId &&
        !isFriendlyConversationIdValid(friendlyConversationId)),
  );

  // Due to pagination selected conversation is not always available in the list of conversations
  // so, if it is not in the list we will load it by id directly with the same filers
  const params = useMemo(() => {
    return {
      filter: conversationIdFilters,
      limit: 1,
      skip: shouldSkipQuery,
      desc: false,
      fetchPolicy: 'cache-first',
      includeDeleted: true,
      includeAttachments,
    };
  }, [conversationIdFilters, shouldSkipQuery, includeAttachments]);

  const { cacheManager, conversations, loading: isLoading } = useConversations(params);

  useEffect(() => {
    if (!includeAttachments) {
      return;
    }

    return subscribeToAppEvent(
      AppEvents.ConversationAttachmentsChanged,
      ({ attachments, conversationId: updatedConversationId }) => {
        if (!conversationId || updatedConversationId !== conversationId) {
          return;
        }

        cacheManager.update((existingData) => {
          const currentConversation = existingData.getConversation.records[0];

          if (
            !currentConversation ||
            currentConversation.conversationId !== updatedConversationId
          ) {
            return;
          }

          currentConversation.attachments = [
            ...attachments,
            ...(currentConversation.attachments ?? []).filter(
              (existingAttachment) =>
                !attachments.some(
                  (incomingAttachment) =>
                    incomingAttachment.fileKey === existingAttachment.fileKey,
                ),
            ),
          ] as typeof currentConversation.attachments;
        });
      },
    );
  }, [cacheManager, conversationId, includeAttachments]);

  return {
    selectedConversation: conversations?.[0] ?? null,
    isLoading,
  };
};
